import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Plane, MapPin, Search, Star, 
  Users, Clock, TrendingUp
} from 'lucide-react';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';
import SiteHeader from '@/components/SiteHeader';

// Top 10 Busiest US Airports with detailed guides
const featuredAirports = [
  {
    code: 'ATL',
    name: 'Hartsfield-Jackson Atlanta International',
    city: 'Atlanta',
    state: 'GA',
    rank: 1,
    passengers: '93.7M',
    highlight: "World's busiest airport, Delta's largest hub",
    tips: 'Use South security checkpoint for shorter lines'
  },
  {
    code: 'LAX',
    name: 'Los Angeles International',
    city: 'Los Angeles',
    state: 'CA',
    rank: 2,
    passengers: '88.1M',
    highlight: 'Busiest on the West Coast, 9 terminals',
    tips: 'Take FlyAway bus from Union Station to avoid parking'
  },
  {
    code: 'ORD',
    name: "O'Hare International",
    city: 'Chicago',
    state: 'IL',
    rank: 3,
    passengers: '83.2M',
    highlight: 'Major United and American hub',
    tips: 'Terminal 5 requires ATS train - allow extra time'
  },
  {
    code: 'DFW',
    name: 'Dallas/Fort Worth International',
    city: 'Dallas',
    state: 'TX',
    rank: 4,
    passengers: '73.4M',
    highlight: "American's largest hub, larger than Manhattan",
    tips: 'Use Skylink train to move between terminals post-security'
  },
  {
    code: 'DEN',
    name: 'Denver International',
    city: 'Denver',
    state: 'CO',
    rank: 5,
    passengers: '69.3M',
    highlight: 'Iconic tent roof, United and Southwest hub',
    tips: 'A Line train from downtown is faster than driving'
  },
  {
    code: 'JFK',
    name: 'John F. Kennedy International',
    city: 'New York',
    state: 'NY',
    rank: 6,
    passengers: '62.5M',
    highlight: "Busiest international gateway to the US",
    tips: 'Take AirTrain to avoid NYC traffic'
  },
  {
    code: 'SFO',
    name: 'San Francisco International',
    city: 'San Francisco',
    state: 'CA',
    rank: 7,
    passengers: '57.5M',
    highlight: 'United hub, known for fog delays',
    tips: 'BART runs direct to airport, check fog advisories'
  },
  {
    code: 'SEA',
    name: 'Seattle-Tacoma International',
    city: 'Seattle',
    state: 'WA',
    rank: 8,
    passengers: '50.0M',
    highlight: 'Alaska Airlines hub, Pacific Northwest gateway',
    tips: 'Light Rail runs 24/7 from downtown Seattle'
  },
  {
    code: 'LAS',
    name: 'Harry Reid International',
    city: 'Las Vegas',
    state: 'NV',
    rank: 9,
    passengers: '52.7M',
    highlight: 'Gateway to Vegas, slot machines in terminal',
    tips: 'Southwest hub - Terminal 1 can be crowded'
  },
  {
    code: 'MCO',
    name: 'Orlando International',
    city: 'Orlando',
    state: 'FL',
    rank: 10,
    passengers: '50.6M',
    highlight: 'Gateway to Disney and Florida theme parks',
    tips: 'New Terminal C has automated train connection'
  }
];

// Additional US Airports
const additionalUSAirports = [
  { code: 'MIA', name: 'Miami International', city: 'Miami', state: 'FL' },
  { code: 'PHX', name: 'Phoenix Sky Harbor', city: 'Phoenix', state: 'AZ' },
  { code: 'EWR', name: 'Newark Liberty International', city: 'Newark', state: 'NJ' },
  { code: 'CLT', name: 'Charlotte Douglas International', city: 'Charlotte', state: 'NC' },
  { code: 'MSP', name: 'Minneapolis-Saint Paul International', city: 'Minneapolis', state: 'MN' },
  { code: 'DTW', name: 'Detroit Metropolitan', city: 'Detroit', state: 'MI' },
  { code: 'BOS', name: 'Boston Logan International', city: 'Boston', state: 'MA' },
  { code: 'PHL', name: 'Philadelphia International', city: 'Philadelphia', state: 'PA' },
  { code: 'IAH', name: 'George Bush Intercontinental', city: 'Houston', state: 'TX' },
  { code: 'FLL', name: 'Fort Lauderdale-Hollywood', city: 'Fort Lauderdale', state: 'FL' },
  { code: 'SAN', name: 'San Diego International', city: 'San Diego', state: 'CA' },
  { code: 'DCA', name: 'Ronald Reagan Washington National', city: 'Washington', state: 'DC' },
  { code: 'IAD', name: 'Washington Dulles International', city: 'Washington', state: 'DC' },
  { code: 'BWI', name: 'Baltimore/Washington International', city: 'Baltimore', state: 'MD' },
  { code: 'TPA', name: 'Tampa International', city: 'Tampa', state: 'FL' }
];

export default function AirportsIndexPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFeatured = featuredAirports.filter(airport =>
    airport.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    airport.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    airport.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAdditional = additionalUSAirports.filter(airport =>
    airport.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    airport.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    airport.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <SEOHead
        title="US Airport Guides — Crowd Levels, Parking, Terminals"
        description="Detailed guides for the busiest US airports (ATL, LAX, ORD, DFW, JFK, DEN, SFO, SEA, LAS, MCO, MIA and more): live crowd predictions, parking, terminals, dining, and arrival tips."
        path="/airports"
        keywords="airport guides, US airports, ATL guide, LAX guide, JFK guide, ORD guide, DFW guide, airport parking, airport terminals"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Airport Guides', url: '/airports' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'US Airport Guides',
            url: 'https://howbusyismyairport.com/airports',
          },
        ]}
      />
      {/* Header */}
      <SiteHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-500 to-blue-600 text-white py-10 sm:py-14">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <MapPin className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Airport Guides</h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-6">
              Comprehensive guides for the busiest airports in the United States. 
              Terminal maps, security tips, parking, dining, and more.
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by airport code, city, or name..."
                className="w-full h-12 pl-12 pr-4 rounded-xl bg-white text-slate-800 placeholder-slate-400 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Featured Airports - Top 10 */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Star className="w-5 h-5 text-amber-500" />
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
              Top 10 Busiest US Airports
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFeatured.map((airport, index) => (
              <motion.div
                key={airport.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/airports/${airport.code.toLowerCase()}`}>
                  <Card className="border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-blue-200 transition-all h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 rounded-lg bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                            {airport.rank}
                          </span>
                          <div>
                            <h3 className="font-bold text-lg text-slate-800 font-mono">
                              {airport.code}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {airport.city}, {airport.state}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">
                          {airport.passengers}
                        </span>
                      </div>
                      
                      <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {airport.name}
                      </p>
                      
                      <p className="text-xs text-slate-500 mb-3">
                        {airport.highlight}
                      </p>
                      
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          {airport.tips}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          {filteredFeatured.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              No airports found matching "{searchTerm}"
            </div>
          )}
        </section>

        {/* Additional US Airports */}
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-slate-500" />
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
              More US Airports
            </h2>
          </div>
          
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {filteredAdditional.map((airport) => (
                  <Link 
                    key={airport.code}
                    to={`/airports/${airport.code.toLowerCase()}`}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-mono font-bold text-blue-600 w-10">
                      {airport.code}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-slate-700 truncate">{airport.city}, {airport.state}</p>
                      <p className="text-xs text-slate-400 truncate">{airport.name}</p>
                    </div>
                  </Link>
                ))}
              </div>
              
              {filteredAdditional.length === 0 && !searchTerm && (
                <div className="text-center py-4 text-slate-500">
                  No additional airports to show
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Quick Stats */}
        <section className="mb-12">
          <Card className="border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div>
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">2.9B+</p>
                  <p className="text-sm text-slate-500">Annual US Passengers</p>
                </div>
                <div>
                  <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">45,000+</p>
                  <p className="text-sm text-slate-500">Daily US Flights</p>
                </div>
                <div>
                  <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-800">331+</p>
                  <p className="text-sm text-slate-500">Airports Supported</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <div className="text-center py-6">
          <p className="text-slate-600 mb-4">Ready to check how busy your airport will be?</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plane className="w-5 h-5" />
            Check Airport Busyness
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-4">
            <Link to="/" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Home</Link>
            <Link to="/airports" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Airport Guides</Link>
            <Link to="/blog" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Blog</Link>
            <Link to="/faq" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">FAQ</Link>
            <Link to="/about" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">About</Link>
            <Link to="/privacy" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="text-sm text-slate-600 hover:text-blue-600 transition-colors">Terms</Link>
          </div>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Airport Busyness Predictor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
